import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import { Stack } from "@mui/material";
import htmlParse from 'html-react-parser';
import { createRef, useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { ActionTypeEnum, StatusEnum } from "../../../common/IExportNotificationMsg";
import { DRCXConfigurationModel } from "../../../common/models/drcx/DRCXConfigurationModel";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../../../components/full-screen-dialog/FullScreenDialog";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import ProgressIndicator from "../../../components/progress-indicator/ProgressIndicator";
import SettingsBar from "../../../components/settings-bar/SettingsBar";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useProgressContext } from "../../../contexts/progress-context/ProgressContext";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useReportAdHocData } from "../../../queries/report/UseReportAdHocData";
import { useReportDeferredData } from "../../../queries/report/UseReportDeferredData";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { IReportModel } from "../../../services/reporting-data-service/Models";
import ReportSection from "../ReportSection";
import { ReportSectionDataType } from "../ReportViewState";
import { createGridData } from "../ReportViewUtils";
import { DefaultGridRef } from "../../../components/default-grid/DefaultGrid";

export type ReportViewDialogInitValues = {
    configuration?: DRCXConfigurationModel;
    fileStoreID?: number;
    title?: string | null;
};

export type ReportViewDialogResultValues = {};

export type ReportViewDialogResultReason = FullScreenDialogCloseReason | "ok";

export type ReportViewDialogResult = DialogResult<ReportViewDialogResultValues, ReportViewDialogResultReason>;

export type ReportViewDialogProps = DialogProps<ReportViewDialogInitValues, ReportViewDialogResult>;

export default function ReportViewDialog({
    open,
    onClose,
    values
}: ReportViewDialogProps): JSX.Element {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [userState] = useUserContext();
    const { confirm } = useConfirmation();
    const [fileStoreID, setFileStoreID] = useState<number | undefined>();
    const [reportSections, setReportSections] = useState<Array<ReportSectionDataType> | undefined>();
    const [progressState, actionStarted] = useProgressContext();
    const downloadUid = useRef<string>(undefined);
    const progress = progressState.items.find(o => o.uid === downloadUid.current);
    const title = values?.title ?? "";

    const {
        isFetching: isFetchingData,
    } = useQueryNotifications(useReportAdHocData)({
        groupID: routeContext.groupID as number,
        configuration: values?.configuration as DRCXConfigurationModel,
        enabled: values?.configuration !== undefined,
        onSuccess: (data: IReportModel | undefined) => {
            onFetchSuccess(data);
        }
    });

    const {
        isFetching: isFetchingDeferredData,
    } = useQueryNotifications(useReportDeferredData)({
        groupID: routeContext.groupID as number,
        fileStoreID: fileStoreID as number,
        enabled: (fileStoreID ?? 0) > 0,
        onSuccess: (data: IReportModel | undefined) => {
            setFileStoreID(undefined);
            onFetchSuccess(data);
        }
    });

    useEffect(() => {

        if (values?.fileStoreID ||
            progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed) {

            downloadUid.current = undefined;

            if (
                values?.fileStoreID ||
                (progress?.notificationMsg.action === ActionTypeEnum.Notify &&
                    progress?.notificationMsg.fileStoreID)
            ) {
                setFileStoreID(values?.fileStoreID ?? progress?.notificationMsg.fileStoreID);
            }
        }
    }, [
        values?.fileStoreID,
        progress?.notificationMsg.action,
        progress?.notificationMsg.fileStoreID,
        progress?.notificationMsg.status,
        setFileStoreID
    ]);

    async function onFetchSuccess(data: IReportModel | undefined) {

        if (data?.isMaxDataValuesExceeded === true) {

            // We exceeded the data value limit, so prompt the user to make a decision
            // on how to proceed
            setReportSections(undefined);

            if (data?.estimatedDataValues && data?.maxDataValues) {

                // Prompt the user to decide whether to adjust the duration or edit the configuration
                await showMaxDataValuesLimitDialog(data.maxDataValues, data.estimatedDataValues);
            }
        } else {

            if (data?.isDataDeferred === true) {

                setReportSections(undefined);
                downloadUid.current = data.notificationID;

                actionStarted({
                    uid: downloadUid.current,
                    objectID: routeContext.groupID as number,
                    objectType: eLynxObjectTypeEnum.Group,
                    title: title,
                    notificationMsg: {
                        action: ActionTypeEnum.Notify,
                        notificationID: downloadUid.current,
                        status: StatusEnum.Started,
                        statusMessage: t("progress.sendingRequest"),
                        additionalInfo: "",
                        blobURI: "",
                        percentComplete: 0,
                        temporary: true
                    }
                });

            } else {
                // We didn't exceed the data value limit and execution wasn't deferred, so proceed as normal
                // by shaping the data in a way the grid can display it
                const reportSections = data?.reportData.map(model => {
                    const { rows, cols } = createGridData(model);

                    return {
                        name: model.tableName,
                        columnDefs: cols,
                        rowData: rows,
                        visible: true,
                        showFooter: model.showFooter,
                        ref: createRef<DefaultGridRef | null>()
                    } as ReportSectionDataType;

                });

                setReportSections(reportSections);
            }
        }
    }

    function handleOnClose(reason: ReportViewDialogResultReason) {
        onClose({ reason: reason });
    }

    async function showMaxDataValuesLimitDialog(dataValuesLimit: number, estimatedDataValues: number) {
        const million: number = 1000000;
        const dataValuesLimitMil: string = (dataValuesLimit / million).toFixed(0);
        const dataValuesEstimateMil: string = (estimatedDataValues / million).toFixed(2);

        const message: string = t("dialogs.configDataLimitsDialog.messagePreview", {
            numDataValues: dataValuesEstimateMil,
            dataLimit: dataValuesLimitMil
        });

        await confirm({
            dialogTitle: t("dialogs.configDataLimitsDialog.title"),
            confirmationBodyComponent: htmlParse(message) as JSX.Element,
            showConfirmationText: true,
            showYesButton: false,
            showNoButton: false,
            showCloseButton: true,
        });

        // can't do anything else, so might as well close the dialog
        handleOnClose("ok");
    }

    const showLoading = isFetchingData ||
        isFetchingDeferredData;
    const defaultPageSize = userState.userSettings?.gridRowCount ?? 50;

    return (
        <FullScreenDialog open={open} onClose={handleOnClose}>
            <SettingsBar
                title={title}
                onClose={() => handleOnClose("ok")}
                hideAction={true}
            />
            <ProgressIndicator showTemporary={true} />
            <LoadingZone isLoading={showLoading}>
                <ContentContainer>
                    <ContentSection>
                        {!isFetchingData && !isFetchingDeferredData &&
                            <Stack
                                spacing={2}
                                sx={{
                                    height: 1,
                                    overflowY: "auto"
                                }}>

                                {reportSections?.map((reportSection, index) => {
                                    return (
                                        <ReportSection
                                            ref={reportSection.ref}
                                            key={index}
                                            pageSize={defaultPageSize}
                                            name={reportSection.name}
                                            rowData={reportSection.rowData}
                                            columnDefs={reportSection.columnDefs}
                                            fitToView={reportSections?.length === 1}
                                            showFooter={reportSection.showFooter}
                                        />
                                    );
                                })}
                            </Stack>}
                    </ContentSection>
                </ContentContainer>
            </LoadingZone>
        </FullScreenDialog>
    );
}

export type ReportViewDialogState = DialogState<ReportViewDialogInitValues, ReportViewDialogResult>;
export type BindReportViewDialogReturn = BindDialogReturn<ReportViewDialogInitValues, ReportViewDialogResult>;

export function useReportViewDialogState(): ReportViewDialogState {
    return useDialogState();
}

export function bindReportViewDialog(props: ReportViewDialogState): BindReportViewDialogReturn {
    return bindDialog(props);
}
